import { useEffect, useReducer, useRef, useState } from "react";
import { Play, Pause, Square, Trash2 } from "lucide-react";
import {
  recorderReducer,
  initialRecorderState,
} from "@a2/core";
import type { UseExamSessionReturn } from "../../../lib/exam/useExamSession";

const MAX_RECORDING_SECONDS = 60;
const TOTAL_BARS = 50;

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      // Some Safari versions throw on isTypeSupported for unknown strings.
    }
  }
  return undefined;
}

function formatMmSs(seconds: number): string {
  const mm = Math.floor(seconds / 60).toString().padStart(2, "0");
  const ss = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

export type DuoRecordPanelProps = {
  session: UseExamSessionReturn;
};

/**
 * DUO chrome record widget — blue 64px-tall pill below the question card.
 *
 *   idle     → red record button + faint waveform + 00:00 / 01:00
 *   recording → red square stop button + animated meter + live elapsed
 *   stopped  → trash · play · filled waveform · actual duration
 *
 * Local recorder state is owned by `recorderReducer` from @a2/core (idle/
 * recording/stopped). The captured blob is mirrored into `session.recordedBlob`
 * so chrome swaps + jumpToIndex see it; the live MediaStream is mirrored
 * into `session.recordedStream` for any future cross-component visualiser.
 */
export function DuoRecordPanel({ session }: DuoRecordPanelProps) {
  const [recState, recDispatch] = useReducer(recorderReducer, initialRecorderState);
  const recording = recState.kind === "recording";
  const isStopped = !recording && session.recordedBlob !== null;

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [clipDuration, setClipDuration] = useState(0);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const playbackRef = useRef<HTMLAudioElement | null>(null);
  const recordingDurationRef = useRef(0);

  useEffect(() => {
    if (!recording) return;
    const start = Date.now();
    const handle = setInterval(() => {
      const dt = (Date.now() - start) / 1000;
      setElapsedSeconds(dt);
      recordingDurationRef.current = dt;
    }, 100);
    return () => clearInterval(handle);
  }, [recording]);

  useEffect(() => {
    if (!session.recordedBlob) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      setIsPlaying(false);
      setPlaybackPosition(0);
      return;
    }
    const url = URL.createObjectURL(session.recordedBlob);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.recordedBlob]);

  useEffect(() => {
    if (!recording) return;
    const t = setTimeout(() => stopRecording(), MAX_RECORDING_SECONDS * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  async function startRecording() {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError(
        "Geen toegang tot microfoon. Controleer uw browserinstellingen.",
      );
      return;
    }
    streamRef.current = stream;
    session.setRecordedStream(stream);

    const mime = pickRecorderMime();
    let rec: MediaRecorder;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      session.setRecordedStream(null);
      setError("Opname wordt door deze browser niet ondersteund.");
      return;
    }

    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      session.setRecordedStream(null);
      const blobType = rec.mimeType || mime || "";
      const blob = blobType
        ? new Blob(chunksRef.current, { type: blobType })
        : new Blob(chunksRef.current);
      chunksRef.current = [];
      if (blob.size === 0) {
        setError("Geen audio opgenomen. Probeer het opnieuw.");
        recDispatch({ kind: "cancel" });
        return;
      }
      setClipDuration(recordingDurationRef.current);
      session.setRecordedBlob(blob);
      // Bridge the captured Blob into the recorder reducer's stopped
      // state. The reducer tracks bytes (Uint8Array) for cross-platform
      // parity with native, but we pass an empty placeholder here — the
      // session.recordedBlob is the real handoff to the submit pipeline.
      recDispatch({
        kind: "stop",
        blob: new Uint8Array(0),
        mimeType: blobType,
      });
    };
    rec.start(1000);
    recRef.current = rec;
    setElapsedSeconds(0);
    recDispatch({ kind: "start" });
    session.onRecordingChange(true);
  }

  function stopRecording() {
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    session.onRecordingChange(false);
  }

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const [trashHovered, setTrashHovered] = useState(false);
  const [trashConfirmOpen, setTrashConfirmOpen] = useState(false);

  // Reset local panel state whenever the displayed question changes.
  useEffect(() => {
    if (recRef.current && recRef.current.state !== "inactive") {
      try {
        recRef.current.stop();
      } catch {
        // stop() can throw if already inactive — safe to ignore.
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recDispatch({ kind: "cancel" });
    recDispatch({ kind: "reset" });
    setElapsedSeconds(0);
    setClipDuration(0);
    setIsPlaying(false);
    setPlaybackPosition(0);
    setError(null);
    setTrashHovered(false);
    setTrashConfirmOpen(false);
    recordingDurationRef.current = 0;
  }, [session.displayedIndex]);

  function confirmDelete() {
    if (playbackRef.current) {
      try {
        playbackRef.current.pause();
      } catch {
        // pause can throw if the element is mid-load; safe to ignore.
      }
    }
    setIsPlaying(false);
    setPlaybackPosition(0);
    setClipDuration(0);
    setTrashConfirmOpen(false);
    setTrashHovered(false);
    session.deleteRecording();
    recDispatch({ kind: "reset" });
  }

  function togglePlayback() {
    const a = playbackRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      a.pause();
      setIsPlaying(false);
    }
  }

  const buttonAriaLabel = isStopped
    ? "Opname verwijderen"
    : recording
      ? "Stop opname"
      : "Start opname";
  const buttonOnClick = isStopped
    ? () => setTrashConfirmOpen((p) => !p)
    : recording
      ? stopRecording
      : startRecording;

  return (
    <div className="mx-auto w-full max-w-[1200px] flex-shrink-0 rounded-lg border border-[var(--duo-card-border)] bg-[var(--duo-card)] p-6 shadow-[var(--duo-card-shadow)]">
      <div
        data-skin="duo"
        className="flex h-20 w-[520px] max-w-full items-center gap-5 rounded-lg bg-[var(--duo-blue)] px-6"
      >
        <div
          className="duo-tooltip-host"
          onMouseEnter={() => isStopped && setTrashHovered(true)}
          onMouseLeave={() => isStopped && setTrashHovered(false)}
        >
          <button
            type="button"
            onClick={buttonOnClick}
            aria-label={buttonAriaLabel}
            aria-expanded={isStopped ? trashConfirmOpen : undefined}
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--duo-red)] transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2"
          >
            {recording ? (
              <>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-full bg-[var(--duo-red)] opacity-60"
                  style={{ animation: "duo-pulse 1.4s ease-out infinite" }}
                />
                <Square
                  size={14}
                  strokeWidth={0}
                  fill="#ffffff"
                  aria-hidden="true"
                />
              </>
            ) : isStopped ? (
              <Trash2 size={18} strokeWidth={2.25} stroke="#ffffff" aria-hidden="true" />
            ) : (
              <span
                className="block h-3 w-3 rounded-full bg-white"
                aria-hidden="true"
              />
            )}
          </button>
          {!isStopped && !recording ? (
            <span className="duo-tooltip" role="tooltip">Opnemen</span>
          ) : null}
          {isStopped && trashHovered && !trashConfirmOpen ? (
            <span
              role="tooltip"
              className="pointer-events-none absolute top-[calc(100%+6px)] left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-sm bg-[var(--duo-tooltip-bg)] px-2 py-1 text-[12px] font-medium text-[var(--duo-tooltip-text)]"
              style={{ animation: "duo-tooltip-in 150ms ease-out" }}
            >
              Opname verwijderen
            </span>
          ) : null}
          {isStopped && trashConfirmOpen ? (
            <div
              role="dialog"
              aria-modal="false"
              aria-label="Opname verwijderen?"
              className="absolute top-[calc(100%+8px)] left-0 z-40 flex flex-col gap-2 rounded-md bg-[var(--duo-orange)] p-3 text-[var(--duo-orange-text)] shadow-md"
              style={{ animation: "duo-tooltip-in 150ms ease-out" }}
            >
              <p className="whitespace-nowrap text-[12px] font-medium">
                Weet je zeker dat je de opname wilt verwijderen?
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTrashConfirmOpen(false)}
                  className="rounded-sm bg-white/90 px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--duo-grey-cell-text)] transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="rounded-sm bg-[var(--duo-orange-hover)] px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--duo-orange-text)] transition-colors hover:bg-[var(--duo-orange-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
                >
                  Verwijderen
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {!isStopped ? <VerticalVuMeter stream={recording ? session.recordedStream : null} /> : null}

        <div className="flex flex-1 items-center gap-4">
          {isStopped ? (
            <>
              <button
                type="button"
                onClick={togglePlayback}
                aria-label={isPlaying ? "Pauze" : "Speel opname af"}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[var(--duo-blue)] transition-colors hover:bg-[var(--duo-grey-cell)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2"
              >
                {isPlaying ? (
                  <Pause size={18} strokeWidth={2} aria-hidden="true" />
                ) : (
                  <Play
                    size={18}
                    strokeWidth={0}
                    fill="currentColor"
                    aria-hidden="true"
                  />
                )}
              </button>
              <span className="text-[14px] tabular-nums text-[var(--duo-blue-text)]">
                {formatMmSs(playbackPosition)}
              </span>
              <div className="relative h-6 flex-1">
                <FilledWaveform progress={clipDuration > 0 ? playbackPosition / clipDuration : 0} />
              </div>
              <span className="text-[14px] tabular-nums text-[var(--duo-blue-text)]">
                {formatMmSs(clipDuration)}
              </span>
            </>
          ) : (
            <>
              <span className="text-[14px] tabular-nums text-[var(--duo-blue-text)]">
                {recording ? formatMmSs(elapsedSeconds) : "00:00"}
              </span>
              <div className="relative h-6 flex-1">
                {recording ? (
                  <ElapsedProgress fraction={elapsedSeconds / MAX_RECORDING_SECONDS} />
                ) : (
                  <IdleWaveform />
                )}
              </div>
              <span className="duo-tooltip-host inline-block">
                <span className="text-[14px] tabular-nums text-[var(--duo-blue-text)]">
                  {formatMmSs(MAX_RECORDING_SECONDS)}
                </span>
                <span className="duo-tooltip" role="tooltip">
                  Maximale opnameduur
                </span>
              </span>
            </>
          )}
        </div>
      </div>
      {error ? (
        <p
          role="alert"
          className="mx-auto mt-3 max-w-[880px] text-center text-sm text-[var(--duo-red)]"
        >
          {error}
        </p>
      ) : null}
      {blobUrl ? (
        <audio
          ref={playbackRef}
          src={blobUrl}
          playsInline
          preload="auto"
          onTimeUpdate={(e) =>
            setPlaybackPosition((e.currentTarget.currentTime ?? 0))
          }
          onEnded={() => {
            setIsPlaying(false);
            setPlaybackPosition(0);
          }}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          className="sr-only"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

/** Vertical multi-color VU meter — segmented amplitude indicator. */
function VerticalVuMeter({ stream }: { stream: MediaStream | null }) {
  const SEGMENTS = 10;
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!stream) {
      setLevel(0);
      return;
    }
    let raf = 0;
    let cancelled = false;
    const AC =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return;
    const ac = new AC();
    let src: MediaStreamAudioSourceNode;
    try {
      src = ac.createMediaStreamSource(stream);
    } catch {
      void ac.close();
      return;
    }
    const analyser = ac.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.7;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      if (cancelled) return;
      analyser.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i] ?? 0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length) / 255;
      setLevel(Math.min(1, rms * 4));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      try {
        src.disconnect();
        analyser.disconnect();
      } catch {
        // disconnect can throw if already disconnected; safe to ignore.
      }
      void ac.close();
    };
  }, [stream]);

  const litCount = Math.round(level * SEGMENTS);
  return (
    <div
      role="meter"
      aria-label="Volumeniveau"
      aria-valuenow={litCount}
      aria-valuemin={0}
      aria-valuemax={SEGMENTS}
      className="flex h-12 w-3 flex-col-reverse items-stretch justify-start gap-[2px]"
    >
      {Array.from({ length: SEGMENTS }, (_, i) => {
        const colour =
          i < 6
            ? "var(--duo-meter-mid)"
            : i < 9
              ? "var(--duo-meter-high)"
              : "var(--duo-red)";
        const lit = i < litCount;
        return (
          <span
            key={i}
            className="block flex-1 rounded-[1px]"
            style={{
              background: colour,
              opacity: lit ? 1 : 0.22,
              transition: "opacity 80ms linear",
            }}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
}

function ElapsedProgress({ fraction }: { fraction: number }) {
  const clamped = Math.max(0, Math.min(1, fraction));
  return (
    <div className="relative h-1 w-full overflow-hidden rounded-full bg-[var(--duo-blue-soft)]">
      <span
        className="absolute inset-y-0 left-0 block rounded-full bg-[var(--duo-blue-text)]"
        style={{
          width: `${clamped * 100}%`,
          transition: "width 100ms linear",
        }}
        aria-hidden="true"
      />
    </div>
  );
}

function FilledWaveform({ progress }: { progress: number }) {
  const clamped = Math.max(0, Math.min(1, progress));
  const cutoff = Math.floor(clamped * TOTAL_BARS);
  return (
    <div className="flex h-full items-center justify-between gap-[2px]">
      {Array.from({ length: TOTAL_BARS }, (_, i) => {
        const phase = Math.sin(i * 0.45) + 0.6 * Math.sin(i * 0.13 + 1.2);
        const height = 8 + Math.abs(phase) * 10;
        return (
          <span
            key={i}
            className="block w-[3px] rounded-sm bg-[var(--duo-blue-text)]"
            style={{
              height: `${height}px`,
              opacity: i < cutoff ? 1 : 0.5,
            }}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
}

function IdleWaveform() {
  return (
    <div className="flex h-full items-center justify-between gap-[2px]">
      {Array.from({ length: TOTAL_BARS }, (_, i) => {
        const phase = (Math.sin(i * 0.6) + Math.sin(i * 0.21)) * 0.5;
        const height = 6 + Math.abs(phase) * 8;
        return (
          <span
            key={i}
            className="block w-[3px] rounded-sm bg-[var(--duo-blue-text)]"
            style={{ height: `${height}px`, opacity: 0.25 }}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
}
